/**
 * Garage-mode camera framing (pure maths, no three.js): where the orbit camera stands for each part of the
 * menu, and how far back it has to be for the car (or one wheel) to fill the free part of the screen — the
 * screen minus the menu's strips, as fractions of the viewport. The car is centred in that free part by
 * sliding the look point across (screen right) and the camera up or down (a pure vertical shift, which keeps
 * a low angle low).
 */

/** The menu's categories; each one has its own camera shot. */
export type GarageView = 'paint' | 'finish' | 'wheels' | 'rim' | 'neon' | 'ride' | 'camber' | 'track' | 'livery';

export interface Shot {
  /** Camera direction from the subject in bay terms: `along` = toward the bay mouth, `side` = across (both unit-ish). */
  along: number;
  side: number;
  /** Elevation (degrees). */
  pitch: number;
  /** Whole car, or the wheel nearest the camera. */
  subject: 'car' | 'wheel';
  /** Dim the work lamps (the underglow wants dusk). */
  dusk?: boolean;
}

/**
 * The shots. The car stands nose-in (its tail at the open mouth) between two 1.1 m walls, so the low shots
 * come in from the mouth; the high side shot looks over the wall at the whole profile.
 */
export const SHOTS: Record<GarageView, Shot> = {
  paint: { along: 0.36, side: 0.93, pitch: 22, subject: 'car' },
  finish: { along: 0.64, side: 0.77, pitch: 16, subject: 'car' },
  wheels: { along: 0.8, side: 0.6, pitch: 9, subject: 'wheel' },
  rim: { along: 0.8, side: 0.6, pitch: 9, subject: 'wheel' },
  neon: { along: 0.93, side: 0.37, pitch: 6, subject: 'car', dusk: true },
  ride: { along: 0.86, side: 0.51, pitch: 8, subject: 'car' },
  camber: { along: 0.99, side: 0.14, pitch: 5, subject: 'car' },
  track: { along: 0.97, side: 0.25, pitch: 5, subject: 'car' },
  // The gang livery (online, WP-X4): high over the wall, the whole profile, as the paint.
  livery: { along: 0.3, side: 0.95, pitch: 28, subject: 'car' },
};

/** The free part of the screen: fractions of the viewport the menu covers on each edge. */
export interface Insets {
  l: number;
  r: number;
  t: number;
  b: number;
}

export interface FrameIn {
  /** Subject centre on the ground, and the height of its middle (m). */
  x: number;
  z: number;
  midY: number;
  /** Half extents along the subject's own axes: length (heading), width, height (m). */
  hl: number;
  hw: number;
  hh: number;
  heading: number;
  /** Unit ground direction from the subject to the camera. */
  dx: number;
  dz: number;
  pitchDeg: number;
  /** Vertical field of view (degrees) and width / height. */
  fovDeg: number;
  aspect: number;
  insets: Insets;
  /** How much of the free part the subject should fill (0..1). */
  fill: number;
}

export interface FrameOut {
  distance: number;
  /** Ground point the rig looks at, and the look height. */
  tx: number;
  tz: number;
  lookY: number;
}

const DEG = Math.PI / 180;

/** Distance and look point that fit the subject into the free part of the screen, centred. */
export function frameShot(f: FrameIn, zoom = 1, out: FrameOut = { distance: 0, tx: 0, tz: 0, lookY: 0 }): FrameOut {
  const p = f.pitchDeg * DEG;
  const ty = Math.tan((f.fovDeg * DEG) / 2);
  const tx = ty * f.aspect;
  // Screen up on the ground points away from the camera; screen right is (up.z… ) as the camera rig has it.
  const ux = -f.dx;
  const uz = -f.dz;
  const rx = -uz;
  const rz = ux;
  const fx = Math.sin(f.heading);
  const fz = Math.cos(f.heading);
  const sx = fz;
  const sz = -fx;
  const across = f.hl * Math.abs(fx * rx + fz * rz) + f.hw * Math.abs(sx * rx + sz * rz);
  const depth = f.hl * Math.abs(fx * ux + fz * uz) + f.hw * Math.abs(sx * ux + sz * uz);
  const tall = f.hh * Math.cos(p) + depth * Math.sin(p);
  const wFree = Math.max(0.2, 1 - f.insets.l - f.insets.r);
  const hFree = Math.max(0.2, 1 - f.insets.t - f.insets.b);
  // The near side is closer than the centre: push back by part of the depth so it still fits.
  const near = depth * Math.cos(p) * 0.55;
  const fit = Math.max(across / (tx * f.fill * wFree), tall / (ty * f.fill * hFree)) + near;
  const distance = Math.max(2.6, fit * zoom);
  // Centre of the free part in NDC (-1..1); move the look point so the subject lands there.
  const cx = f.insets.l - f.insets.r;
  const cy = f.insets.b - f.insets.t;
  const shiftX = cx * distance * tx;
  out.distance = distance;
  out.tx = f.x - rx * shiftX;
  out.tz = f.z - rz * shiftX;
  out.lookY = f.midY - (cy * distance * ty) / Math.max(0.2, Math.cos(p));
  return out;
}

/** Rig yaw offset (radians, relative to `rigYaw`) that puts the camera in ground direction (dx, dz) from its subject. */
export function yawFor(dx: number, dz: number, rigYaw: number): number {
  let off = Math.atan2(-dx, -dz) - rigYaw;
  while (off > Math.PI) off -= Math.PI * 2;
  while (off < -Math.PI) off += Math.PI * 2;
  return off;
}

/** `to` moved by whole turns to be nearest `from` (so a glide takes the short way round). */
export function nearestTurn(from: number, to: number): number {
  const t = Math.PI * 2;
  return to + Math.round((from - to) / t) * t;
}
